import { supabase, SUPABASE_BUCKET } from '../services/supabaseClient';
import { apiPublic } from '../services/apiPublic';
import { compressImageFile } from './blobToBase64';

// Event media is immutable — cache hard for one year.
const ONE_YEAR = '31536000';

// Slot minting is a tiny JSON POST. Run more of them in parallel than file
// uploads so Heroku latency is paid once up front, not between every photo.
const SLOT_CONCURRENCY = 8;

const extOf = (file) => (file.name.split('.').pop() || 'bin').toLowerCase();

const runWithConcurrency = async (items, concurrency, worker) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const runOne = async () => {
    while (true) {
      const i = nextIndex;
      nextIndex += 1;
      if (i >= items.length) return;
      // eslint-disable-next-line no-await-in-loop -- per-worker serial is intentional
      results[i] = await worker(items[i], i);
    }
  };

  const workerCount = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(Array.from({ length: workerCount }, runOne));
  return results;
};

async function getSlot({ eventId, eventCode, mediaType, ext, token }) {
  const { data } = await apiPublic.post('/media/upload-url', {
    eventId,
    eventCode,
    mediaType,
    ext,
    token,
  });
  return data; // { mediaId, path, token }
}

async function confirm(meta, path) {
  const { data } = await apiPublic.post('/media/confirm', { ...meta, path });
  return data; // serialized record with url + thumbUrl
}

// Shrink camera-roll photos before they hit the network. A 4–12 MB phone
// photo becomes ~200–600 KB at 1920px / q=0.82 and looks the same on a gallery.
async function prepareFile(file, mediaType) {
  if (mediaType === 'image') {
    return compressImageFile(file);
  }
  return file;
}

async function putToSupabase(file, slot) {
  const { error } = await supabase.storage
    .from(SUPABASE_BUCKET)
    .uploadToSignedUrl(slot.path, slot.token, file, {
      cacheControl: ONE_YEAR,
      contentType: file.type || undefined,
    });
  if (error) throw error;
}

/**
 * Upload an array of files directly to Supabase Storage via server-minted signed
 * URLs, then record each file's metadata on the server.
 *
 * Slots are minted in a parallel burst first so Heroku round-trips don't sit
 * in the middle of each file upload.
 *
 * @param {File[]} files
 * @param {object} meta - { eventId, mediaType, eventCode, guestName, guestPhone, ... }
 * @param {string} qrToken - QR/JWT token from URL params
 * @param {object} options
 * @param {number} [options.concurrency=4]
 * @param {function} [options.onFileDone] - called with (index, serverRecord) after each file
 * @returns {Promise<object[]>} Array of server records (url + thumbUrl per file)
 */
export async function uploadMany(files, meta, qrToken, { concurrency = 4, onFileDone } = {}) {
  if (!files || files.length === 0) return [];

  const prepared = await Promise.all(files.map((file) => prepareFile(file, meta.mediaType)));

  const slots = await runWithConcurrency(prepared, SLOT_CONCURRENCY, (file) =>
    getSlot({
      eventId: meta.eventId,
      eventCode: meta.eventCode,
      mediaType: meta.mediaType,
      ext: extOf(file),
      token: qrToken,
    })
  );

  const out = new Array(files.length);
  const failures = [];

  await runWithConcurrency(prepared, concurrency, async (file, i) => {
    try {
      await putToSupabase(file, slots[i]);
      out[i] = await confirm(meta, slots[i].path);
      onFileDone?.(i, out[i]);
    } catch (error) {
      failures.push({ index: i, error });
    }
  });

  if (failures.length > 0) {
    const error = new Error(`${failures.length} of ${files.length} file(s) failed to upload`);
    error.failures = failures;
    throw error;
  }

  return out;
}
